import React, { useEffect, useMemo, useState } from 'react';
import DataGrid, { SortColumn } from 'react-data-grid';

import {
  ApolloError,
  Box,
  Grid,
  GridItem,
  HStack,
  LoadingIndicator,
  SimpleDrawer,
  Spacer,
  Text,
  VStack,
} from '../../../core-ui';
import formatCurrency from '../../../core/support/formatCurrency';
import { parseInput } from '../../uiold/CurrencyInput';
import { getRedemptionAmount } from '../../../core/support/getRedemptionAmount';

import { CreateSalesNoteFooter } from './CreateSalesNoteFooter';
import { FeeCheckbox } from './FeeCheckbox';
import { Row, Rows } from './helpers';
import { makeColumns } from './makeColumns';
import { SalesNoteList } from './SalesNoteList';

import {
  GetFeesAndSales,
  GetFeesAndSales_redemptions as GQLRedemption,
} from './__generated__/GetFeesAndSales';
import { useQuery } from '@apollo/client';
import { GET_SALES_NOTES } from './graphql';
import { GetSalesNotesBySalesId } from './__generated__/GetSalesNotesBySalesId';
import { useAuthState } from '../../AuthState';

function updateInvoiceTotal(input: {
  row: Row;
  redemptionsById: RedemptionsById;
}): Row | null {
  const { row, redemptionsById } = input;
  if (!row) {
    return null;
  }
  const {
    vendorName,
    vendorId,
    salesId,
    category,
    sales,
    percentage,
    flatFeeTotal,
    flatFees,
    redemptions,
    salesNoteCount,
  } = row;
  let newSalesTotal = parseInput(sales);
  let invoiceRedemptionTotal = 0;
  // Adjust number sign based on redemption column's `invoiceTotalModifier` property
  const newRedemptions = Object.fromEntries(
    Object.entries(redemptions).map(([key, value]) => {
      const redemption = redemptionsById[key];
      if (redemption) {
        const { addAbsValueToSalesTotal } = redemption;
        const parsedValue = parseInput(String(value));
        if (addAbsValueToSalesTotal) {
          newSalesTotal += Math.abs(parsedValue);
        }
        const amountToAddToInvoice = getRedemptionAmount({
          amountInCents: parsedValue,
          redemption,
        });
        invoiceRedemptionTotal += amountToAddToInvoice;
        const isNone = redemption.invoiceTotalModifier === 'none';
        const formattedValue = formatCurrency(
          isNone ? Math.abs(parsedValue) : amountToAddToInvoice,
        );
        return [key, formattedValue];
      }
      return [key, value];
    }),
  );

  const newPercentage = !isNaN(Number(percentage)) ? Number(percentage) : 0;
  const newPctOfSales = newSalesTotal * (percentage / 100);
  const newInvoiceTotal = Number(
    Object.values(flatFees).reduce((prev, curr) => {
      const numberValue = Number.isNaN(curr) ? 0 : Number(curr);
      return prev + numberValue;
    }, newPctOfSales + invoiceRedemptionTotal),
  );

  const newRow = {
    sales: formatCurrency(parseInput(sales)),
    percentage: newPercentage,
    pctOfSales: newPctOfSales,
    invoiceTotal: newInvoiceTotal,
    vendorName,
    vendorId,
    salesId,
    category,
    flatFeeTotal,
    salesTotal: newSalesTotal,
    redemptions: newRedemptions,
    flatFees,
    salesNoteCount,
  };

  return newRow;
}

function updateTableRow(input: {
  index: number;
  rowFunction: (input: {
    row: Row;
    redemptionsById: RedemptionsById;
  }) => Row | null;
  rows: Rows;
  saveVendorSalesRow: (row: Row) => void;
  setRows: (rows: Rows) => void;
  redemptionsById: RedemptionsById;
}) {
  const {
    index,
    rowFunction,
    rows,
    saveVendorSalesRow,
    setRows,
    redemptionsById,
  } = input;
  const selectedRow = rows[index] as Row;
  if (!selectedRow) {
    setRows(rows as Array<Row>);
    return;
  }
  const newRow = rowFunction({ row: selectedRow, redemptionsById });
  if (newRow) {
    setRows([...rows.slice(0, index), newRow, ...rows.slice(index + 1)]);
    saveVendorSalesRow(newRow);
  }
}

type RedemptionsById = {
  [id: string]: GQLRedemption;
};

type Props = {
  queryData: GetFeesAndSales;
  rows: Rows;
  saveVendorSalesRow: (row: Row) => Promise<void>;
  selectedMarketDate: string;
  selectedMarketId: string;
  setRows: (rows: Rows) => void;
};

export function SalesTable(props: Props) {
  const {
    queryData,
    rows,
    saveVendorSalesRow,
    selectedMarketDate,
    selectedMarketId,
    setRows,
  } = props;

  const flatFees = queryData.flatFees ?? [];

  const [showDrawer, setShowDrawer] = useState(false);
  const [showNotesDrawer, setShowNotesDrawer] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);
  const [sortColumns, setSortColumns] = useState<ReadonlyArray<SortColumn>>([]);
  const [flatFeesById, setFlatFeesById] = useState<Record<string, number>>(
    flatFees.reduce((prev, curr) => {
      const feeId = curr.id;
      const cell = selectedRow?.flatFees[feeId];
      if (!cell) {
        return prev;
      }
      return { ...prev, [feeId]: cell };
    }, {}),
  );
  const auth = useAuthState();
  const myUserId = auth.user.id;

  function getComparator(sortColumn: string): (a: Row, b: Row) => number {
    switch (sortColumn) {
      case 'vendorName':
        return (a, b) => {
          return a[sortColumn].localeCompare(b[sortColumn]);
        };
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  const sortedRows = useMemo((): ReadonlyArray<Row> => {
    if (sortColumns.length === 0) {
      return rows;
    }

    return rows.sort((a, b) => {
      for (const sortColumn of sortColumns) {
        const comparator = getComparator(sortColumn.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sortColumn.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [rows, sortColumns]);

  useEffect(() => {
    const flatFees = queryData.flatFees ?? [];
    setFlatFeesById(
      flatFees.reduce((prev, curr) => {
        const feeId = curr.id;
        const cell = selectedRow?.flatFees[feeId];
        if (!cell) {
          return prev;
        }
        return { ...prev, [feeId]: cell };
      }, {}),
    );
  }, [selectedRow, queryData]);

  const { data, error, refetch } = useQuery<GetSalesNotesBySalesId>(
    GET_SALES_NOTES,
    {
      fetchPolicy: 'cache-and-network',
      variables: { salesId: selectedRow?.salesId ?? '' },
    },
  );

  if (error) {
    return <ApolloError error={error} />;
  }
  if (!data) {
    return <LoadingIndicator />;
  }

  const salesNotes = data.salesNotesBySalesId ?? [];

  const columns = makeColumns({
    queryData,
    onFlatFeeClick: (row) => {
      setSelectedRow(row);
      setShowDrawer(true);
    },
    onNotesClick: (row) => {
      setSelectedRow(row);
      setShowNotesDrawer(true);
    },
  });

  const redemptionsById = (queryData.redemptions ?? []).reduce<RedemptionsById>(
    (prev, curr) => {
      return { ...prev, [curr.id]: curr };
    },
    {},
  );

  return (
    <Box my="0.5rem" maxHeight="60vh" overflowY="auto">
      <DataGrid
        className="rdg-light"
        columns={columns}
        rowKeyGetter={(row) => row.vendorId}
        rows={sortedRows}
        onRowsChange={async (rows, data) => {
          const index = data.indexes[0] ?? 0;
          updateTableRow({
            index,
            rowFunction: updateInvoiceTotal,
            rows,
            saveVendorSalesRow,
            setRows,
            redemptionsById,
          });
        }}
        sortColumns={sortColumns}
        onSortColumnsChange={setSortColumns}
      />
      {selectedRow && (
        <SimpleDrawer
          drawerTitle={selectedRow.vendorName ?? ''}
          isOpen={showDrawer}
          onClose={async () => {
            const flatFeeTotal = Object.values(flatFeesById).reduce(
              (prev, curr) => prev + curr,
              0,
            );
            const newSelectedRow = updateInvoiceTotal({
              row: {
                ...selectedRow,
                flatFees: {
                  ...selectedRow.flatFees,
                  ...flatFeesById,
                },
                flatFeeTotal,
              },
              redemptionsById,
            });
            const newSelectedRowIndex = rows.findIndex(
              (row) => row?.salesId === selectedRow.salesId,
            );
            const newRows =
              newSelectedRow != null
                ? [
                    ...rows.slice(0, newSelectedRowIndex),
                    newSelectedRow,
                    ...rows.slice(newSelectedRowIndex + 1),
                  ]
                : rows;
            setRows(newRows);
            setShowDrawer(false);
            if (newSelectedRow != null) {
              await saveVendorSalesRow(newSelectedRow);
            }
          }}
          size="sm"
          returnFocusOnClose={false}
          bodyProps={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            paddingBottom: 0,
          }}
        >
          <VStack>
            {flatFees.map((fee) => {
              const feeId = fee.id;
              return (
                <FeeCheckbox
                  key={feeId}
                  feeColumn={fee}
                  flatFee={Number(selectedRow.flatFees[feeId]) ?? 0}
                  updateFlatFee={(amountInCents) => {
                    setFlatFeesById({
                      ...flatFeesById,
                      [feeId]: amountInCents,
                    });
                  }}
                />
              );
            })}
            <HStack>
              <Text>{t('Total')}</Text>
              <Spacer />
              <Text>
                {formatCurrency(
                  Object.values(flatFeesById).reduce(
                    (prev, curr) => prev + curr,
                    0,
                  ),
                )}
              </Text>
            </HStack>
          </VStack>
        </SimpleDrawer>
      )}
      {selectedRow && (
        <SimpleDrawer
          drawerTitle={
            `${
              queryData.season?.markets.find(
                (market) => market.id === selectedMarketId,
              )?.name ?? ''
            } | ${selectedMarketDate.split('T')[0]} | ${
              selectedRow.vendorName
            }` ?? ''
          }
          isOpen={showNotesDrawer}
          onClose={async () => {
            const flatFeeTotal = Object.values(flatFeesById).reduce(
              (prev, curr) => prev + curr,
              0,
            );
            const newSelectedRow = updateInvoiceTotal({
              row: {
                ...selectedRow,
                flatFees: {
                  ...selectedRow.flatFees,
                  ...flatFeesById,
                },
                flatFeeTotal,
              },
              redemptionsById,
            });
            const newSelectedRowIndex = rows.findIndex(
              (row) => row?.salesId === selectedRow.salesId,
            );
            const newRows =
              newSelectedRow != null
                ? [
                    ...rows.slice(0, newSelectedRowIndex),
                    newSelectedRow,
                    ...rows.slice(newSelectedRowIndex + 1),
                  ]
                : rows;
            setRows(newRows);
            setShowNotesDrawer(false);
            if (newSelectedRow != null) {
              await saveVendorSalesRow(newSelectedRow);
            }
          }}
          size="sm"
          returnFocusOnClose={false}
          bodyProps={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            paddingBottom: '10px',
          }}
        >
          <Grid maxHeight="100%" templateRows="repeat(1, 1fr)" gap={4}>
            <GridItem rowSpan={4} maxHeight="100%">
              <SalesNoteList salesNotes={salesNotes} />
            </GridItem>
            <GridItem>
              {selectedRow.salesId && (
                <CreateSalesNoteFooter
                  refetch={refetch}
                  salesId={selectedRow.salesId}
                  userId={myUserId}
                />
              )}
            </GridItem>
          </Grid>
        </SimpleDrawer>
      )}
    </Box>
  );
}
